import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { RefreshControl } from 'react-native';
import {
    BodyMediumRegular,
    FootnoteEmphasized,
    H2Regular,
    isWeb,
    LabelEmphasized,
    Private,
    ScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { formatDollars } from '../../../common/utils/bankingUtils';
import { getGoalTaskCounts } from '../../../common/utils/goalUtils';
import { selectedGoalAtom } from '../../state/GoalAtoms';
import type { IGoal } from '../../types/Goal';
import {
    GoalCardContext,
    GoalCategoryDescription,
    GoalTabType,
} from '../../types/Goal';
import DummyGoalCard from './DummyGoalCard';
import GoalCategoryIcon from './GoalCategoryIcon';

interface GoalCardProps {
    goal: IGoal;
    context: GoalCardContext;
}

export const GoalCard = ({ goal, context }: GoalCardProps) => {
    const setSelectedGoal = useSetAtom(selectedGoalAtom);

    const { category } = goal;
    const { completedTaskCount, totalTaskCount } = getGoalTaskCounts(goal);

    return (
        <XStack
            alignItems="center"
            gap={10}
            padding={10}
            borderRadius={12}
            backgroundColor="$white"
            borderColor="$gray200"
            borderWidth={1}
            key={goal.description}
            rotate={
                context === GoalCardContext.COMPLETED_SHEET ? '-2deg' : '0deg'
            }
            animation="button"
            pressStyle={{
                scale: 0.95,
            }}
            {...(context === GoalCardContext.LIST && {
                cursor: 'pointer',
                hoverStyle: { backgroundColor: '$gray100' },
                onPress: () => {
                    setSelectedGoal(goal);
                    router.navigate('/goals/goal-details');
                },
            })}
        >
            <GoalCategoryIcon
                goalCategoryDescription={
                    category?.description as GoalCategoryDescription
                }
            />
            <YStack flex={1}>
                <Private>
                    <BodyMediumRegular numberOfLines={2} marginBottom={2}>
                        {goal.description}
                    </BodyMediumRegular>
                </Private>
                <LabelEmphasized color="$gray1000">
                    {completedTaskCount === totalTaskCount
                        ? 'Tasks complete'
                        : `${completedTaskCount}/${totalTaskCount} tasks`}
                </LabelEmphasized>
            </YStack>
            {goal.targetAmount != null && goal.targetAmount > 0 && (
                <View marginLeft="auto" padding={8}>
                    <FootnoteEmphasized>
                        {formatDollars(goal.targetAmount, false)}
                    </FootnoteEmphasized>
                </View>
            )}
        </XStack>
    );
};

interface NoGoalsForTypeProps {
    tabType: GoalTabType;
}

const NoGoalsForType = ({ tabType }: NoGoalsForTypeProps) => {
    const headerText =
        tabType === GoalTabType.COMPLETE
            ? 'Your goals are still a work in progress.'
            : 'Keep it up!';

    const subText =
        tabType === GoalTabType.COMPLETE
            ? "Keep doing what you're doing!"
            : "You've completed all your goals. Chat with your Guide to explore what's next.";

    return (
        <YStack
            height="100%"
            alignItems="center"
            justifyContent="center"
            gap={40}
            paddingHorizontal={20}
        >
            <YStack width={256} gap={8}>
                <H2Regular textAlign="center">{headerText}</H2Regular>
                <BodyMediumRegular textAlign="center">
                    {subText}
                </BodyMediumRegular>
            </YStack>

            {tabType === GoalTabType.ACTIVE && (
                <View>
                    <DummyGoalCard
                        goalDescription="Buy a house"
                        goalCategoryDescription={
                            GoalCategoryDescription.MILESTONES
                        }
                        completedTaskCount={3}
                        totalTaskCount={4}
                        rotate="4deg"
                    />
                    <DummyGoalCard
                        goalDescription="Travel the world"
                        goalCategoryDescription={
                            GoalCategoryDescription.FUNDAMENTALS
                        }
                        completedTaskCount={1}
                        totalTaskCount={3}
                        marginTop={-10}
                        rotate="-5deg"
                    />
                </View>
            )}
        </YStack>
    );
};

interface GoalsListProps {
    tabType: GoalTabType;
    goals: IGoal[];
    onRefresh: () => void;
    isLoadingGoalGroups: boolean;
}

const GoalsList = ({
    tabType,
    goals,
    onRefresh,
    isLoadingGoalGroups,
}: GoalsListProps) => {
    return (
        <View flex={1}>
            {goals.length ? (
                <ScrollView
                    showsVerticalScrollIndicator={isWeb}
                    refreshControl={
                        <RefreshControl
                            refreshing={isLoadingGoalGroups}
                            onRefresh={onRefresh}
                        />
                    }
                >
                    <YStack gap={6} paddingHorizontal={20} paddingBottom={20}>
                        {goals.map((goal) => {
                            return (
                                <GoalCard
                                    key={goal.id}
                                    goal={goal}
                                    context={GoalCardContext.LIST}
                                />
                            );
                        })}
                    </YStack>
                </ScrollView>
            ) : (
                <NoGoalsForType tabType={tabType} />
            )}
        </View>
    );
};

export default GoalsList;
